import { DatePipe } from '@angular/common';
import { Component, ElementRef, ViewChild } from '@angular/core';
import { DataService } from '@app/core/services/api';
import { ToastComponent } from '@app/shared/components/toast/toast.component';
import { ExcelService } from '@core/services/excel/excel.service';
import * as _ from 'lodash';
import moment from 'moment';
import { firstValueFrom } from 'rxjs';

@Component({
  standalone: false,
  selector: 'app-upload-measurements-modal',
  templateUrl: './upload-measurements-modal.component.html',
  styleUrls: ['./upload-measurements-modal.component.scss']
})
export class UploadMeasurementsModalComponent {
  @ViewChild('uploadMeasurementsModal') uploadModal: ElementRef;
  @ViewChild(ToastComponent) wdprtoast: ToastComponent;

  pipe = new DatePipe('en-US');
  uploadErrors = {
    errMsg: '',
    pernrs: []
  };
  disabledDone = true;

  constructor(
    private dataService: DataService,
    private excelService: ExcelService
  ) { }

  async onFileDropped(files: any[]) {
    await this.uploadFile(files[0]);
  }

  async onBrowseFiles(event: any) {
    event.preventDefault();
    const file = event.target.files[0];
    await this.uploadFile(file);
  }

  async uploadFile(file: any) {
    try {
      this.resetErrors();
      const fileReader = new FileReader();
      fileReader.readAsBinaryString(file);
      fileReader.onload = async (event) => {
        this.handleCsvUploadProcess(event.target.result);
      };
    } catch (error) {
      console.error(error);
      this.displayUploadErrors(error, []);
      this.wdprtoast.open('Something went wrong. Please try again.', false);
      this.disabledDone = true;
    }
  }

  async handleCsvUploadProcess(result: any) {
    const measurements = this.formatMeasurements(result);
    const errors = this.validateMeasurementFormats(measurements);
    if (measurements.length > 0 && errors.length === 0) {
      await this.uploadMeasurements(measurements);
      this.disabledDone = false;
    } else {
      this.displayUploadErrors('Please check all required fields', errors);
      this.wdprtoast.open('Something went wrong. Please try again.', false);
    }

    this.resetUpload();
  }

  formatMeasurements(result: any): any[] {
    const measurements = [];
    let results = this.excelService.extractCsvToJson(result);
    results = results.filter(item => item['Scan_ID'] !== undefined);
    /* istanbul ignore else*/
    if (results.length > 0) {
      results.forEach(item => {
        const castKeys = [
          'Scan_ID',
          'Scan_Image_File_Name',
          'Scan_DateTime',
          'Personnel_Number',
          'Role',
          'Status',
          'Scanner_Location',
          'Site'
        ];
        const payload: any = _.pick(item, castKeys);
        const measurments: any = _.omit(item, castKeys);
        payload.measurements = measurments;
        measurements.push(payload);
      });
    }

    return measurements;
  }

  validateMeasurementFormats(measurements: any[]): string[] {
    const errLogs = [];
    if (measurements.length === 0) {
      errLogs.push('Upload atleast 1 cast measurements');
    } else {
      let row = 1;
      for (const measurement of measurements) {
        row++;

        if (measurement['Scan_ID'].trim() == '') {
          errLogs.push(`Scan_ID at row ${row} is required`);
        } else {
          measurement['Scan_ID'] = this.excelService.cleanStrValue(measurement['Scan_ID']);
        }

        if (measurement['Personnel_Number'].trim() == '') {
          errLogs.push(`Personnel_Number at row ${row} is required`);
        }

        // Accept both single- and double-digit hours (e.g. "09:10:00" and "9:10:00")
        const dateFormat1Strict = moment(measurement['Scan_DateTime'], 'YYYY-MM-DD HH:mm:ss', true);
        const dateFormat1Loose = moment(measurement['Scan_DateTime'], 'YYYY-MM-DD H:mm:ss', true);
        const dateFormat1 = dateFormat1Strict.isValid() ? dateFormat1Strict : dateFormat1Loose;
        const dateFormat2 = moment(measurement['Scan_DateTime'], 'M/DD/YYYY H:mm:ss A');
        const dateFormat3 = moment(measurement['Scan_DateTime'], 'M/DD/YYYY H:mm');

        if (measurement['Scan_DateTime'].trim() == '') {
          errLogs.push(`Scan_DateTime at row ${row} is required`);
        } else if (!dateFormat1.isValid() && !dateFormat2.isValid() && !dateFormat3.isValid()) {
          errLogs.push(`Scan_DateTime at row ${row} is invalid date format`);
        } else {
          if (dateFormat2) {
            // Convert to standard date format
            measurement['Scan_DateTime'] = dateFormat2.format('YYYY-MM-DD H:mm:ss');
          }

          if (dateFormat3) {
            // Convert to standard date format
            measurement['Scan_DateTime'] = dateFormat3.format('YYYY-MM-DD H:mm:ss');
          }

          // Convert to UTC Format
          measurement['Scan_DateTime'] = moment(new Date(measurement['Scan_DateTime'])).utc().format();
        }

        if (measurement['Site'].trim() == '') {
          errLogs.push(`Site at row ${row} is required`);
        }

        if (measurement['Role'].trim() == '') {
          errLogs.push(`Role at row ${row} is required`);
        }
      }
    }

    return errLogs;
  }

  async uploadMeasurements(measurements: any[]) {
    const response = await firstValueFrom(this.dataService.uploadCastMeasurements(measurements));
    if (response.pernr && response.pernr.length > 0) {
      this.displayUploadErrors(response.message, response.pernr);
    } else {
      this.resetErrors();
    }

    this.wdprtoast.open('CSV File successfully uploaded.', true);
  }

  displayUploadErrors(errMsg: string, pernrs: string[]) {
    this.uploadErrors.errMsg = errMsg;
    this.uploadErrors.pernrs = pernrs;
  }

  resetUpload() {
    const file: any = document.getElementById('file-input');
    file.value = null;
  }

  resetErrors() {
    this.uploadErrors = {
      errMsg: '',
      pernrs: []
    };
  }

  open() {
    this.resetErrors();
    this.resetUpload();
    this.uploadModal.nativeElement.open = true;
  }

  close() {
    this.resetErrors();
    this.resetUpload();
    this.uploadModal.nativeElement.open = false;
  }
}
